// components/taxonomy/CategoryPickerModal.tsx (UPDATED: correct normalized fields + refresh hook)
//
// ✅ Uses CategoryDoc.nameNormalized (not `normalized`)
// ✅ exactMatchExists checks against doc ids + nameNormalized
// ✅ After suggesting a category, calls optional onAfterChange() so parent can reload Firestore-approved list
// ✅ Keeps pending flow intact

import React, { useMemo, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import type { CategoryDoc } from "@/lib/taxonomy";
import { normalizeLabel, suggestCategory } from "@/lib/taxonomy";
import { db } from "@/lib/firebase";

type Props = {
  visible: boolean;
  onClose: () => void;

  categories: CategoryDoc[];
  loading?: boolean;

  createdByUid: string;

  selectedCategoryId?: string | null;

  onSelectApproved: (category: { id: string; name: string }) => void;
  onSelectPending: (category: { id: string; name: string }) => void; // pending suggestion

  /** optional: parent can refresh Firestore list after changes */
  onAfterChange?: () => Promise<void> | void;
};

function getNormalizedForSearch(c: CategoryDoc): string {
  // ✅ new schema
  if (typeof c.nameNormalized === "string" && c.nameNormalized) return c.nameNormalized;

  // backward-compatible fallback if older memory uses a different key
  const maybeOld = c as unknown as { normalized?: string };
  if (typeof maybeOld.normalized === "string" && maybeOld.normalized) return maybeOld.normalized;

  return normalizeLabel(c.name);
}

export default function CategoryPickerModal({
  visible,
  onClose,
  categories,
  loading,
  createdByUid,
  selectedCategoryId,
  onSelectApproved,
  onSelectPending,
  onAfterChange,
}: Props) {
  const insets = useSafeAreaInsets();
  const [search, setSearch] = useState("");

  const rawSearch = search.trim();
  const normalizedSearch = useMemo(() => normalizeLabel(rawSearch), [rawSearch]);

  const filtered = useMemo(() => {
    if (!normalizedSearch) return categories;
    return categories.filter((c) => getNormalizedForSearch(c).includes(normalizedSearch));
  }, [categories, normalizedSearch]);

  const exactMatchExists = useMemo(() => {
    if (!normalizedSearch) return false;

    return categories.some((c) => {
      // consider exact match by id OR by normalized name field
      return c.id === normalizedSearch || getNormalizedForSearch(c) === normalizedSearch;
    });
  }, [categories, normalizedSearch]);

  const canAdd = !!normalizedSearch && !exactMatchExists;
  const keyboardBehavior =
    Platform.OS === "ios"
      ? "padding"
      : Platform.OS === "android"
      ? "height"
      : undefined;
  const sheetBottomPadding = Math.max(insets.bottom, 16);
  const listBottomPadding = sheetBottomPadding + 56;

  function handleClose() {
    Keyboard.dismiss();
    onClose();
  }

  async function handleAddNew() {
    if (!normalizedSearch) return;

    Keyboard.dismiss();

    try {
      const res = await suggestCategory(db, {
        name: rawSearch,
        createdBy: createdByUid,
      });

      if (res.kind === "existsApproved") {
        onSelectApproved({ id: res.categoryId, name: res.categoryName });
        handleClose();
        return;
      }

      // Pending suggestion created (or already pending)
      onSelectPending({ id: res.categoryId, name: res.categoryName });
      handleClose();

      Alert.alert(
        "Submitted for approval",
        `"${rawSearch}" will be reviewed. Your card can still be published.`
      );

      // Optional refresh (when admin approves later this won't auto-refresh,
      // but at least the local list can re-sync after writes)
      await Promise.resolve(onAfterChange?.());
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to add category.");
    }
  }

  function handlePick(item: CategoryDoc) {
    Keyboard.dismiss();
    onSelectApproved({ id: item.id, name: item.name });
    handleClose();
  }

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      statusBarTranslucent
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView
        behavior={keyboardBehavior}
        style={styles.keyboardAvoider}
      >
        <View style={styles.backdrop}>
          <View style={[styles.card, { paddingBottom: sheetBottomPadding }]}>
          <View style={styles.header}>
            <Text style={styles.title}>Select Category</Text>

            <Pressable onPress={handleClose} style={styles.closeBtn} hitSlop={10}>
              <Text style={styles.closeText}>Close</Text>
            </Pressable>
          </View>

          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search category or type to add…"
            style={styles.input}
            autoCapitalize="words"
            autoCorrect={false}
          />

          {loading ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator />
              <Text style={styles.loadingText}>Loading categories…</Text>
            </View>
          ) : (
            <>
              {canAdd ? (
                <Pressable onPress={handleAddNew} style={styles.addBtn}>
                  <Text style={styles.addBtnText}>Add “{rawSearch}”</Text>
                  <Text style={styles.addSubText}>(Will be pending approval)</Text>
                </Pressable>
              ) : null}

              <FlatList
                data={filtered}
                keyExtractor={(item) => item.id}
                keyboardShouldPersistTaps="handled"
                style={styles.list}
                contentContainerStyle={[
                  styles.listContent,
                  { paddingBottom: listBottomPadding },
                ]}
                renderItem={({ item }) => {
                  const selected = selectedCategoryId === item.id;
                  return (
                    <Pressable
                      onPress={() => handlePick(item)}
                      style={[styles.row, selected && styles.rowSelected]}
                    >
                      <Text style={styles.rowText}>{item.name}</Text>
                      {selected ? <Text style={styles.badge}>Selected</Text> : null}
                    </Pressable>
                  );
                }}
                ListEmptyComponent={
                  <View style={styles.empty}>
                    <Text style={styles.emptyText}>
                      No categories found. Type a name above to add it.
                    </Text>
                  </View>
                }
              />
            </>
          )}
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  keyboardAvoider: {
    flex: 1,
  },
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "flex-end",
  },
  card: {
    backgroundColor: "white",
    padding: 16,
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    maxHeight: "85%",
  },
  list: {
    flexGrow: 0,
  },
  listContent: {
    paddingTop: 0,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  title: { fontSize: 18, fontWeight: "700" },
  closeBtn: { paddingVertical: 6, paddingHorizontal: 10 },
  closeText: { fontSize: 14, fontWeight: "600" },

  input: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 10,
  },

  addBtn: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
  },
  addBtnText: { fontSize: 16, fontWeight: "700" },
  addSubText: { marginTop: 2, fontSize: 12, opacity: 0.7 },

  row: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  rowSelected: { backgroundColor: "#f6f6f6" },
  rowText: { fontSize: 15 },

  badge: { fontSize: 12, fontWeight: "700", opacity: 0.7 },

  empty: { padding: 18 },
  emptyText: { opacity: 0.7 },

  loadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 10,
  },
  loadingText: { opacity: 0.7 },
});
